import React from 'react';
import {
  View,
  Text,
  Platform,
  StyleSheet,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import SkeletonPlaceholder from 'react-native-skeleton-placeholder';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme } from '../../theme';
const isWeb = Platform.OS === 'web';
import {
  fs,
  iconSize,
  s,
  v,
} from '../../utils/libs';
import { formatSharePercent } from '../../utils/paymentBreakdown';
import type { DashboardComparisonMetric } from '../../redux/backoffice/dashboardSlice';

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

export type StatCardProps = {
  label: string;
  value: string;
  valuePrefix?: string;
  valueSuffix?: string;
  comparison?: DashboardComparisonMetric;
  comparisonPeriodLabel?: string;
  loading?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  icon?: IconName;
  iconColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  // Makes the whole card tappable and shows a chevron on the right.
  onPress?: () => void;
};

const StatValueSkeleton: React.FC<{ width: number; height: number }> = ({
  width,
  height,
}) => {
  const { theme } = useTheme();

  return (
    <SkeletonPlaceholder
      backgroundColor={theme.colors.border}
      highlightColor={theme.colors.surfaceSecondary}>
      <SkeletonPlaceholder.Item
        width={width}
        height={height}
        borderRadius={s(4)}
      />
    </SkeletonPlaceholder>
  );
};

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  valuePrefix,
  valueSuffix,
  comparison,
  comparisonPeriodLabel,
  loading = false,
  containerStyle,
  icon,
  iconColor,
  backgroundColor,
  borderColor,
  onPress,
}) => {
  const { theme } = useTheme();
  const styles = createStyles(theme);
  const badgeColor = iconColor || theme.colors.primary;
  const comparisonChange = Number(comparison?.change ?? 0);
  const comparisonPercentage = Number(comparison?.percentageChange ?? 0);
  const hasComparison = Boolean(comparison);
  const shouldShowComparison = hasComparison || loading;
  const shouldShowZeroInsteadOfNegativeHundred = comparisonPercentage === -100;
  const displayComparisonPercentage = shouldShowZeroInsteadOfNegativeHundred
    ? 0
    : comparisonPercentage;
  const isPositive =
    comparisonChange >= 0 || shouldShowZeroInsteadOfNegativeHundred;
  const comparisonSign = isPositive ? '+' : '-';
  const comparisonColor = isPositive
    ? theme.colors.successEmerald
    : theme.colors.error;

  const cardStyle = {
    backgroundColor: backgroundColor || theme.colors.surface,
    padding: s(12),
    borderRadius: theme.borderRadius.md,
    // Every card carries the 1px border so tinted and plain cards in the
    // same row or column stay the same height.
    borderWidth: 1,
    borderColor: borderColor || 'transparent',
    flexBasis: '31%',
    maxWidth: '31%',
    ...theme.shadows.sm,
  } as ViewStyle;

  const content = (
    <>
      <View style={styles.labelRow}>
        {icon ? (
          <View
            style={[styles.iconBadge, { backgroundColor: badgeColor + '1A' }]}>
            <MaterialCommunityIcons
              name={icon}
              size={fs(15)}
              color={badgeColor}
            />
          </View>
        ) : null}
        <Text style={styles.label}>{label}</Text>
      </View>
      <View style={styles.valueRow}>
        {valuePrefix ? <Text style={styles.value}>{valuePrefix}</Text> : null}
        {loading ? (
          <StatValueSkeleton width={s(72)} height={v(18)} />
        ) : (
          <Text style={styles.value} numberOfLines={1} adjustsFontSizeToFit>
            {value}
          </Text>
        )}
        {valueSuffix ? <Text style={styles.value}>{valueSuffix}</Text> : null}
      </View>
      {shouldShowComparison ? (
        <View style={styles.comparisonRow}>
          {loading ? (
            <StatValueSkeleton width={s(48)} height={v(12)} />
          ) : (
            <Text style={[styles.comparison, { color: comparisonColor }]}>
              {comparisonSign}
              {formatSharePercent(Math.abs(displayComparisonPercentage) / 100)}
            </Text>
          )}
          {comparisonPeriodLabel ? (
            <Text style={styles.comparisonPeriod}>
              vs {comparisonPeriodLabel}
            </Text>
          ) : null}
        </View>
      ) : null}
    </>
  );

  if (!onPress) {
    return <View style={[cardStyle, containerStyle]}>{content}</View>;
  }

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={`View ${label}`}
      activeOpacity={0.7}
      onPress={onPress}
      style={[cardStyle, styles.pressableCard, containerStyle]}>
      <View style={styles.pressableBody}>{content}</View>
      <MaterialCommunityIcons
        name="chevron-right"
        size={iconSize(20)}
        color={theme.colors.textSecondary}
      />
    </TouchableOpacity>
  );
};

export default StatCard;

const createStyles = (theme: any) =>
  StyleSheet.create({
    pressableCard: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(6),
    },
    pressableBody: {
      flex: 1,
      minWidth: 0,
    },
    labelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(6),
      marginBottom: v(6),
    },
    iconBadge: {
      width: s(24),
      height: s(24),
      borderRadius: s(7),
      alignItems: 'center',
      justifyContent: 'center',
    },
    label: {
      flexShrink: 1,
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(13),
      lineHeight: fs(18),
    },
    valueRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(4),
      ...(isWeb
        ? {
            marginTop: v(10),
          }
        : {}),
    },
    value: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(18),
      lineHeight: fs(20),
      // Lets adjustsFontSizeToFit shrink "KSh 1,234,567.00" inside the row.
      flexShrink: 1,
    },
    comparisonRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: s(4),
      marginTop: v(6),
      flexWrap: 'wrap',
    },
    comparison: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    comparisonPeriod: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
  });
